/**
 * Document Helper Utilities
 * Provides helper functions for document viewing, downloading, and visibility display
 */

import React from 'react';
import PublicIcon from '@mui/icons-material/Public';
import BusinessIcon from '@mui/icons-material/Business';
import GroupIcon from '@mui/icons-material/Group';
import LockIcon from '@mui/icons-material/Lock';
import { getApiClient } from '../../services/api/ApiClient';
import { log } from '../errors/logger';
import { DocumentVisibility } from '../../api';
import { TFunction } from 'i18next';

/**
 * Extract clean document ID from URL or ID string
 * @param documentId - Document ID or URL path
 * @returns Clean document ID
 */
function extractDocumentId(documentId: string): string {
  if (documentId.startsWith('/document/download/')) {
    return documentId.replace('/document/download/', '');
  }
  if (documentId.startsWith('/document/view/')) {
    return documentId.replace('/document/view/', '');
  }
  return documentId;
}

/**
 * Extract filename from Content-Disposition header
 * @param response - HTTP Response with Content-Disposition header
 * @returns Extracted filename or default
 */
function extractFilename(response: Response, defaultName = 'document.txt'): string {
  const contentDisposition = response.headers.get('Content-Disposition');

  if (!contentDisposition) {
    log.warn('No Content-Disposition header received');
    return defaultName;
  }

  log.info('Content-Disposition header received:', { contentDisposition });

  // Try UTF-8 format first (RFC 5987): filename*=UTF-8''example.pdf
  const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;\n]+)/);
  if (utf8Match?.[1]) {
    const filename = decodeURIComponent(utf8Match[1]);
    log.info('Extracted filename from UTF-8 format:', { filename });
    return filename;
  }

  // Fallback to standard format: filename="example.pdf" or filename=example.pdf
  const standardMatch = contentDisposition.match(/filename="?([^";\n]+)"?/);
  if (standardMatch?.[1]) {
    const filename = standardMatch[1];
    log.info('Extracted filename from standard format:', { filename });
    return filename;
  }

  log.warn('Could not extract filename from Content-Disposition', { contentDisposition });
  return defaultName;
}

/**
 * Download a blob with proper cleanup
 * @param blob - Blob to download
 * @param filename - Filename for download
 */
function downloadBlob(blob: Blob, filename: string): void {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();

  // Immediate cleanup (download triggers before revoke)
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
}

/**
 * Download original document file
 * Uses centralized API client for proper authentication and error handling
 * @param documentId - Document ID or download URL
 */
export async function downloadOriginalDocument(documentId: string): Promise<void> {
  try {
    if (!documentId) {
      log.error('No document ID provided');
      return;
    }

    const cleanDocumentId = extractDocumentId(documentId);
    const apiClient = getApiClient();

    // Use API client for proper authentication (cookie-based)
    const response = await apiClient.documents.getDocumentBlob(cleanDocumentId, false);

    const filename = extractFilename(response);
    const blob = await response.blob();

    downloadBlob(blob, filename);

    log.info('Document downloaded successfully', { documentId: cleanDocumentId, filename });
  } catch (error) {
    log.error('Error downloading document', { documentId, error });
    throw error;
  }
}

/**
 * View original document in new window
 * Uses centralized API client for proper authentication and error handling
 * @param documentId - Document ID or view URL
 */
export async function viewOriginalDocument(documentId: string): Promise<void> {
  try {
    if (!documentId) {
      log.error('No document ID provided');
      return;
    }

    const cleanDocumentId = extractDocumentId(documentId);
    const apiClient = getApiClient();

    // Use API client for proper authentication (cookie-based)
    const response = await apiClient.documents.getDocumentBlob(cleanDocumentId, true);

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const newWindow = window.open(url, '_blank');

    if (!newWindow) {
      log.warn('Popup blocked - falling back to download');
      // Fallback: download the document instead
      const filename = extractFilename(response, `document-${cleanDocumentId}`);
      downloadBlob(blob, filename);
      window.URL.revokeObjectURL(url);
      return;
    }

    // Cleanup with proper timing to avoid premature revocation
    // Use longer timeout to ensure content loads, especially for large files
    const cleanupTimer = setTimeout(() => {
      window.URL.revokeObjectURL(url);
      log.info('Cleaned up blob URL after timeout');
    }, 10000); // 10 seconds for safe loading

    // Try to cleanup earlier if window loads successfully
    // This prevents memory leaks while ensuring content is available
    try {
      newWindow.addEventListener('load', () => {
        clearTimeout(cleanupTimer);
        // Small delay after load to ensure rendering completes
        setTimeout(() => {
          window.URL.revokeObjectURL(url);
          log.info('Cleaned up blob URL after window load');
        }, 1000);
      });
    } catch (error) {
      // If we can't attach load listener (cross-origin, etc.), rely on timeout
      log.warn('Could not attach load listener, using timeout cleanup', { error });
    }

    log.info('Document opened for viewing', { documentId: cleanDocumentId });
  } catch (error) {
    log.error('Error viewing document', { documentId, error });
    throw error;
  }
}

// ==================== Visibility Helpers ====================

/**
 * Get icon component for document visibility level
 *
 * @param visibility - The document visibility setting
 * @returns React icon element with appropriate color
 *
 * @example
 * ```tsx
 * const icon = getDocumentVisibilityIcon(DocumentVisibility.Public);
 * // Returns <PublicIcon fontSize="small" color="success" />
 * ```
 */
export const getDocumentVisibilityIcon = (
  visibility: DocumentVisibility
): React.ReactElement => {
  switch (visibility) {
    case DocumentVisibility.Public:
      return React.createElement(PublicIcon, { fontSize: 'small', color: 'success' });
    case DocumentVisibility.Organization:
      return React.createElement(BusinessIcon, { fontSize: 'small', color: 'primary' });
    case DocumentVisibility.Group:
      return React.createElement(GroupIcon, { fontSize: 'small', color: 'info' });
    case DocumentVisibility.Private:
      return React.createElement(LockIcon, { fontSize: 'small', color: 'action' });
    default:
      return React.createElement(BusinessIcon, { fontSize: 'small', color: 'primary' });
  }
};

/**
 * Get human-readable text for document visibility
 *
 * @param doc - Document with visibility and shared_with_groups properties
 * @param t - i18next translation function
 * @returns Localized visibility description
 *
 * @example
 * ```tsx
 * const text = getDocumentVisibilityText(
 *   { visibility: DocumentVisibility.Groups, shared_with_groups: ['team1', 'team2'] },
 *   t
 * );
 * // Returns "Shared with 2 groups" (localized)
 * ```
 */
export const getDocumentVisibilityText = (
  doc: {
    visibility?: DocumentVisibility;
    shared_with_groups?: string[];
  },
  t: TFunction
): string => {
  const visibility = doc.visibility || DocumentVisibility.Organization;

  switch (visibility) {
    case DocumentVisibility.Public:
      return t('documents.visibility.public');
    case DocumentVisibility.Organization:
      return t('documents.visibility.organization');
    case DocumentVisibility.Group:
      return doc.shared_with_groups && doc.shared_with_groups.length > 0
        ? t('documents.visibility.groups_count', { count: doc.shared_with_groups.length })
        : t('documents.visibility.group');
    case DocumentVisibility.Private:
      return t('documents.visibility.private');
    default:
      return t('documents.visibility.organization');
  }
};
